import { memo } from 'react';
import { useTranslation } from 'react-i18next';

import { Loader } from '@speeki/global-ui-components';
import { IconManager, IconName } from '@speeki/icons';

import {
    LanguageSelectInfo,
    LanguageSelectList,
    LanguageSelecWrapper,
    SelectButton,
    SelectIconWrapper,
    SelectItem,
    SelectLabel,
    SelectText,
    SelectWrapper,
} from '@components/molecules/LanguageSelect/LanguageSelect.styled';
import { useLanguageSelect } from '@components/molecules/LanguageSelect/useLanguageSelect';

import {
    currentEnglishLanguageSelect,
    languagesOptions,
} from '@utils/dictionary';

interface SelectIconProps {
    icon: IconName;
    isDisabled?: boolean;
}

const SelectIcon = ({ icon, isDisabled }: SelectIconProps) => (
    <SelectIconWrapper>
        <IconManager
            icon={icon}
            size={24}
            themeColors={{
                default: {
                    'color-1': isDisabled ? 'text.assistive' : 'text.primary',
                },
            }}
        />
    </SelectIconWrapper>
);

export interface DesktopLanguageSelectProps {
    className?: string;
}

export const DesktopLanguageSelect = memo(
    ({ className }: DesktopLanguageSelectProps) => {
        const { isDisabled, isOpen, loading, onChange, selectRef, setIsOpen } =
            useLanguageSelect();
        const currentLanguage = currentEnglishLanguageSelect();
        const { t } = useTranslation();
        return (
            <SelectWrapper className={className}>
                <SelectButton
                    data-testid="w2jE_Qb"
                    disabled={isDisabled}
                    onClick={() => setIsOpen(true)}
                >
                    {loading ? (
                        <Loader color="text_assistive" size={24} />
                    ) : (
                        <SelectIcon
                            icon={'SvgLanguage24'}
                            isDisabled={isDisabled}
                        />
                    )}
                    <SelectLabel data-testid="-GnTg0h" isDisabled={isDisabled}>
                        {currentLanguage?.label}
                    </SelectLabel>
                    <SelectIcon
                        icon={isOpen ? 'SvgChevronUp24' : 'SvgChevronDown24'}
                        isDisabled={isDisabled}
                    />
                </SelectButton>
                {isOpen && (
                    <LanguageSelecWrapper>
                        <LanguageSelectInfo>
                            {t('CHANGE_LANGUAGE_INFO')}
                        </LanguageSelectInfo>
                        <LanguageSelectList ref={selectRef}>
                            {languagesOptions.map(({ key, label, value }) => {
                                const isActive =
                                    currentLanguage?.value === value;

                                return (
                                    <SelectItem
                                        data-testid={`pds2Oa-${value}`}
                                        key={key}
                                        onClick={() => onChange(value)}
                                    >
                                        <SelectText isActive={isActive}>
                                            {label}
                                        </SelectText>
                                        {isActive && (
                                            <IconManager
                                                icon={'SvgTick24'}
                                                size={24}
                                                themeColors={{
                                                    default: {
                                                        'color-1':
                                                            'main.primary',
                                                    },
                                                }}
                                            />
                                        )}
                                    </SelectItem>
                                );
                            })}
                        </LanguageSelectList>
                    </LanguageSelecWrapper>
                )}
            </SelectWrapper>
        );
    },
);
